import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import {
  QueryCache,
  MutationCache,
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query';
import { BrowserRouter } from 'react-router-dom';
import { registerSW } from 'virtual:pwa-register';
// Self-hosted Inter variable font. One import covers all weights and
// the variable axis. No CDN, no privacy concern, ships in the bundle.
import '@fontsource-variable/inter';
import { App } from './App';
import { AppErrorBoundary } from './components/AppErrorBoundary';
import { initSentryFromConfig, reportWebError } from './lib/sentry';
import { bootstrapTheme } from './hooks/useTheme';
import { bootstrapFontScale } from './hooks/useFontScale';
import './styles/index.css';

// Hydrate the theme synchronously before React renders so the first
// paint already has the correct data-theme attribute. Avoids a flash
// of light-on-dark (or vice versa) when the user has picked dark.
bootstrapTheme();
// Same for the chosen text size, so the first paint is already scaled.
bootstrapFontScale();

// Fire-and-forget: monitoring comes up as soon as the runtime config
// answers; the app never waits on it.
void initSentryFromConfig();

const queryClient = new QueryClient({
  // Surface unexpected failures (bugs, malformed responses) to Sentry.
  // ApiErrors are excluded inside reportWebError: they are handled UX
  // (toasts, inline messages) and venue wifi blips must not flood it.
  queryCache: new QueryCache({ onError: reportWebError }),
  mutationCache: new MutationCache({ onError: reportWebError }),
});

const rootEl = document.getElementById('root');
if (!rootEl) {
  throw new Error('root element missing from index.html');
}

createRoot(rootEl).render(
  <StrictMode>
    <AppErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <App />
        </BrowserRouter>
      </QueryClientProvider>
    </AppErrorBoundary>
  </StrictMode>,
);

// PWA service worker registration. Auto-updates on the next page load
// when a new version is built. CLAUDE.md 6.5 calls out offline
// tolerance via the SW; this wires it up.
if (typeof window !== 'undefined') {
  registerSW({ immediate: true });
}
